// Saisie de terrain par la file d'envoi (incrément 6) : chaque volet (coche, annulation, temps, consigne, réserve, levée, mesure,
// pièce posée, photo) range d'abord sa saisie sur le téléphone, puis l'envoie s'il y a du réseau (lib/fileEnvoi.ts). Le volet sait
// ensuite dire exactement ce qui s'est passé : arrivée sur le serveur, gardée sur le téléphone (envoi automatique au retour du
// réseau), ou refusée (motif affiché dans le volet, encore ouvert, pour corriger).
import { useQueryClient } from '@tanstack/react-query';
import { messageErreur } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { reduirePhoto } from '../../../lib/photo.ts';
import { proprioDe, soumettre, type NouvelleOperation, type Soumission } from '../../../lib/fileEnvoi.ts';

export const libelleChantier = (c: { reference: string | null; title: string }) => [c.reference, c.title].filter(Boolean).join(' — ');

/** Ce que le volet affiche quand la saisie est gardée sur le téléphone (pas de réseau, ou réseau perdu pendant l'envoi). */
export const GARDEE = 'Gardé sur ce téléphone : envoi automatique dès le retour du réseau.';

/** Requêtes à rafraîchir après une saisie (le cache « Ma journée » gardé hors connexion se rafraîchit avec). */
const CLES = ['ma-journee', 'chantier', 'chantier-jour', 'chantier-reserves', 'chantier-pieces', 'chantier-avancement', 'planning-chantier', 'equipement', 'carnet',
  'chantier-emplacements'];

export function useSaisie() {
  const { moi } = useSession();
  const qc = useQueryClient();
  const proprio = proprioDe(moi);
  /** Range puis envoie ; « principale » = l'opération dont le refus revient au volet (par défaut la dernière). */
  const envoyer = async (ops: NouvelleOperation[], principale?: number): Promise<Soumission> => {
    const s = await soumettre(proprio, ops, principale);
    if (s.etat !== 'refusee') await Promise.all(CLES.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    return s;
  };
  return { proprio, envoyer };
}

/** Opération « photo » : la photo est réduite (≤ 2000 px, JPEG) AVANT d'être gardée sur le téléphone. */
export async function operationPhoto(o: { chantierId: string; chantierLibelle: string; fichier: File; rattachement: Record<string, string>; libelle: string;
  clientOpId: string; prealable?: boolean }): Promise<NouvelleOperation> {
  const photo = await reduirePhoto(o.fichier);
  return {
    clientOpId: o.clientOpId, type: 'photo', chantierId: o.chantierId, chantierLibelle: o.chantierLibelle, donnees: o.rattachement, libelle: o.libelle,
    photo, prealable: !!o.prealable, etapeId: o.rattachement.etapeId ?? null,
  };
}

/** Motif d'un échec avant l'envoi (photo illisible, stockage du téléphone plein…) : le message en français, sinon le message d'API. */
export const messageSaisie = (e: unknown) =>
  (e instanceof Error && !('status' in e) && e.message ? e.message : messageErreur(e));

/** Phrase de compte rendu des saisies secondaires refusées (photo jointe…) : elles restent dans « Envois refusés ». */
export const texteSecondaires = (s: Soumission) => s.secondairesRefusees.map((o) => `${o.libelle} : ${o.refus?.message ?? 'refusée'} — elle reste dans « Envois refusés ».`);
